/**
 * Cross-tab sync via per-scope BroadcastChannel.
 *
 * Channel name: `edge-workspace:{scope}`
 *
 * Messages:
 *
 *   { type: 'workspace:updated', workspaceRevision: number }
 *     Tree/list changed. Receivers compare against their cached
 *     workspaceRevision and refetch bootstrap if older.
 *
 *   { type: 'layout:updated', layoutId: string, layoutRevision: number }
 *     Content of a specific layout changed. Receivers viewing that layoutId
 *     refetch its content; others can stash the revision for next time.
 */

import type { BootstrapData } from '../types'
import type { WorkspaceStore } from './store'
import type { EnqueueBootstrapOpts } from './bootstrap'
import { shouldRunBootstrap } from './gates'

export type BroadcastMessage =
  | { type: 'workspace:updated'; workspaceRevision: number }
  | { type: 'layout:updated'; layoutId: string; layoutRevision: number }

export interface BroadcastBinding {
  /** Send a message to other tabs subscribed to this scope. */
  post: (message: BroadcastMessage) => void
  /** Tear down the channel and remove the listener. */
  close: () => void
}

export interface OpenBroadcastOptions {
  scope: string
  store: WorkspaceStore
  /**
   * Returns true while a local persistence-queue task is in flight or
   * queued. Used by the dequeue-time gate (`shouldRunBootstrap`) to defer
   * re-bootstrapping mid-mutation — the pending write will either land
   * successfully (and broadcast its own update) or fail (and push the
   * controller stale).
   */
  isPersistenceQueueBusy?: () => boolean
  /**
   * Required: the caller's queued bootstrap entrypoint. Inbound
   * workspace:updated messages enqueue runBootstrap here so init + inbound
   * never race each other on IDB writes.
   */
  enqueueBootstrap: (opts: EnqueueBootstrapOpts) => Promise<BootstrapData | null>
}

/**
 * Open a per-scope BroadcastChannel.
 *
 * On `workspace:updated`, if the incoming revision is higher than the
 * controller's cached revision, re-bootstrap. (Equal or lower revisions
 * mean we already have this state.)
 *
 * On `layout:updated`, the controller doesn't act directly — the per-layout
 * content store listens for those itself. We log so the message is visible
 * for debugging.
 */
/**
 * Dev-only broadcast tracing. Default-on in dev so misrouted/dropped
 * messages are visible without ceremony. Silence with:
 *   localStorage.setItem('edge-debug-broadcast', '0')
 */
function bcastLog(...args: unknown[]): void {
  if (import.meta.env.PROD) return
  if (import.meta.env.MODE === 'test') return
  if (typeof localStorage !== 'undefined' && localStorage.getItem('edge-debug-broadcast') === '0')
    return

  console.log('[bcast:ws]', ...args)
}

export function openBroadcast(opts: OpenBroadcastOptions): BroadcastBinding {
  if (typeof BroadcastChannel === 'undefined') {
    bcastLog('open SKIP — BroadcastChannel undefined', { scope: opts.scope })
    return {
      post: () => {},
      close: () => {},
    }
  }

  const channelName = `edge-workspace:${opts.scope}`
  const channel = new BroadcastChannel(channelName)
  bcastLog('open', { channel: channelName })

  channel.onmessage = (event: MessageEvent<BroadcastMessage>) => {
    const msg = event.data
    if (!msg) {
      bcastLog('inbound DROP — empty data', { channel: channelName })
      return
    }

    if (msg.type === 'workspace:updated') {
      // Every inbound workspace:updated enqueues a bootstrap task. The
      // applicability gate (`shouldRunBootstrap`) runs once at dequeue —
      // if local state has already caught up (or the controller is stale,
      // or the persistence queue is busy) the task no-ops without a fetch.
      const incomingRevision = msg.workspaceRevision
      bcastLog('inbound workspace:updated → enqueueBootstrap', {
        channel: channelName,
        incoming: incomingRevision,
      })
      opts
        .enqueueBootstrap({
          scope: opts.scope,
          store: opts.store,
          // Cross-tab broadcasts must NOT yank the local user out of
          // whatever layout they're viewing, even if the sibling tab
          // created or switched to a new one.
          preserveActiveLayout: true,
          shouldRun: () =>
            shouldRunBootstrap(
              opts.store.getState(),
              incomingRevision,
              opts.isPersistenceQueueBusy?.() ?? false,
            ),
        })
        .then(() => bcastLog('inbound runBootstrap done'))
        .catch((err) => {
          bcastLog('inbound runBootstrap failed', err)
          /* swallow — bootstrap error already updated connectionStatus */
        })
      return
    }
    // 'layout:updated' is handled by the per-layout content store's own channel listener.
    bcastLog('inbound (layout:updated, ignored at controller scope)', msg)
  }

  return {
    post: (message) => {
      try {
        channel.postMessage(message)
        bcastLog('post', { channel: channelName, message })
      } catch (err) {
        bcastLog('post FAILED', { channel: channelName, err })
        /* swallow — channel may be closing */
      }
    },
    close: () => {
      try {
        channel.close()
        bcastLog('close', { channel: channelName })
      } catch {
        /* swallow */
      }
    },
  }
}
